import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Card,
  Button,
  Tag,
  Spin,
  message,
  Modal,
  Form,
  Select,
  Space,
  Statistic,
  Row,
  Col,
  Descriptions,
  Switch,
  Empty,
  DatePicker,
  Table,
} from 'antd';
import {
  ArrowLeftOutlined,
  LineChartOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  ExportOutlined,
  FolderOpenOutlined,
  HistoryOutlined,
  FileTextOutlined,
  DownloadOutlined,
  ThunderboltOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TopBar from '@/Components/TopBar';
import Navbar from "@/Components/navbar";
import { opcuaRead, opcuaHistorical } from '@/services/opcService';
import { opcGetGroups, opcSetPointGroup, opcGetGroupsOfPoint } from '@/services/opcService';
import './index.less';

// ============================================================
// 假接口定义区域 —— 后续需要替换为真实 API 调用
// ============================================================

/**
 * 点位基础信息
 */
interface PointInfo {
  id: string;
  name: string;
  node_id: string;
  group_name: string;
  unit: string;
  status: 'Good' | 'Bad' | 'Uncertain';
  connection_id: string;
}

/**
 * 实时数据点
 */
interface DataPoint {
  time: string;
  value: number;
}

/**
 * 【假接口】获取点位基础信息
 * TODO: 替换为真实 API: GET /api/opc/points/:id
 */
const mockGetPointInfo = async (pointId: string, connectionId: string): Promise<PointInfo> => {
  await new Promise((r) => setTimeout(r, 300));
  // 从缓存中查找
  const cacheKey = `opc_points_${connectionId}`;
  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    const points = JSON.parse(cached);
    const point = points.find((p: any) => p.id === pointId);
    if (point) {
      return {
        id: point.id,
        name: point.name,
        node_id: point.node_id,
        group_name:
          point.group_id ? (() => {
            const groupsKey = `opc_groups_${connectionId}`;
            const groupsRaw = localStorage.getItem(groupsKey);
             
            if (groupsRaw) {
              const groups = JSON.parse(groupsRaw);
              const g = groups.find((g2: any) => g2.id === point.group_id);
              return g?.name || '未分组';
            }
            return '未分组';
          })() : '未分组',
        unit: point.unit,
        status: point.status,
        connection_id: connectionId,
      };
    }
  }
  // 回退假数据
  return {
    id: pointId,
    name: pointId,
    node_id: `ns=1;s=OPC.Unknown.${pointId}`,
    group_name: '未分组',
    unit: '',
    status: 'Good',
    connection_id: connectionId,
  };
};

/**
 * 读取点位实时值（单次）
 * POST /api/opcua/read → { success, value, dataType }
 */
const readPointValue = async (pointId: string): Promise<number | null> => {
  const res = await opcuaRead({ nodeId: pointId });
  const val = parseFloat(res.value);
  return isNaN(val) ? null : val;
};

/**
 * 获取点位历史数据
 * POST /api/opcua/historical → { data: [{ value: { value, dataType }, statusCode, sourceTimestamp }] }
 */
const fetchHistoricalData = async (
  nodeId: string,
  startTime: string,
  endTime: string,
): Promise<DataPoint[]> => {
  const res = await opcuaHistorical({ nodeId, startTime, endTime });
  if (!res.data || res.data.length === 0) return [];
  return res.data
    .filter((d) => d.statusCode?.value === 0) // 仅保留 Good 状态数据
    .map((d) => ({
      time: d.sourceTimestamp,
      value: typeof d.value?.value === 'number' ? d.value.value : parseFloat(d.value?.value) || 0,
    }));
};

/**
 * 导出录制数据为 CSV 文件并触发下载
 */
const exportRecordingCSV = (
  data: { time: string; value: number }[],
  pointName: string,
) => {
  const header = '时间,值';
  const rows = data.map((d) => `${d.time},${d.value}`);
  const csv = [header, ...rows].join('\n');

  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const filename = `${pointName}_${new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)}.csv`;
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  message.success(`数据已保存：${filename}（共 ${data.length} 条）`);
};

// ============================================================
// 统计计算
// ============================================================
const calcStats = (data: number[]) => {
  if (data.length === 0) return { avg: 0, max: 0, min: 0, std: 0 };
  const sum = data.reduce((a, b) => a + b, 0);
  const avg = sum / data.length;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const variance = data.reduce((s, v) => s + (v - avg) ** 2, 0) / data.length;
  const std = Math.sqrt(variance);
  return { avg, max, min, std };
};

// ============================================================
// PointData 组件
// ============================================================
const PointData: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pointId = searchParams.get('point_id') || '';
  const connectionId = searchParams.get('connection_id') || 'default';

  // --- 数据状态 ---
  const [loading, setLoading] = useState(true);
  const [pointInfo, setPointInfo] = useState<PointInfo | null>(null);
  const [groupName, setGroupName] = useState('未分组');

  // --- 实时数据 ---
  const [currentValue, setCurrentValue] = useState<number | null>(null);
  const [realtimeData, setRealtimeData] = useState<DataPoint[]>([]);
  const [realtimeEnabled, setRealtimeEnabled] = useState(true);
  const realtimeTimerRef = useRef<NodeJS.Timeout | null>(null);

  // --- 历史数据 ---
  const [historyData, setHistoryData] = useState<DataPoint[]>([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  // --- 历史数据弹窗 ---
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyModalData, setHistoryModalData] = useState<DataPoint[]>([]);
  const [historyModalLoading, setHistoryModalLoading] = useState(false);
  const [historyTimeRange, setHistoryTimeRange] = useState<[string, string]>([
    new Date(Date.now() - 24 * 3600000).toISOString().split('T')[0],
    new Date().toISOString().split('T')[0],
  ]);

  // --- 记录功能 ---
  const [recording, setRecording] = useState(false);
  const [recordCount, setRecordCount] = useState(0);
  const recordedDataRef = useRef<{ time: string; value: number }[]>([]);

  // --- 分组弹窗 ---
  const [groupList, setGroupList] = useState<{ id: string; name: string }[]>([]);
  const [groupModalOpen, setGroupModalOpen] = useState(false);

  // ==========================================================
  // 初始化加载
  // ==========================================================
  const loadPointInfo = useCallback(async () => {
    setLoading(true);
    try {
      const info = await mockGetPointInfo(pointId, connectionId);
      setPointInfo(info);

      // 从真实 API 获取分组信息
      try {
        const groups = await opcGetGroups(connectionId);
        setGroupList(groups);
        const pointGroups = await opcGetGroupsOfPoint(connectionId, pointId);
        if (pointGroups.length > 0) {
          const g = groups.find((grp) => grp.id === pointGroups[0]);
          const name = g?.name || '未分组';
          setGroupName(name);
        } else {
          setGroupName('未分组');
        }
      } catch {}
    } catch {
      message.error('加载点位信息失败');
    } finally {
      setLoading(false);
    }
  }, [pointId, connectionId]);

  const loadHistoryData = useCallback(async () => {
    try {
      const endTime = new Date().toISOString();
      const startTime = new Date(Date.now() - 1800000).toISOString();
      const data = await fetchHistoricalData(pointId, startTime, endTime);
      setHistoryData(data);
      setHistoryLoaded(true);
    } catch {
      message.error('加载历史数据失败');
    }
  }, [pointId]);

  // 加载弹窗历史数据
  const loadHistoryModalData = useCallback(async () => {
    setHistoryModalLoading(true);
    try {
      const data = await fetchHistoricalData(
        pointId,
        `${historyTimeRange[0]}T00:00:00Z`,
        `${historyTimeRange[1]}T23:59:59Z`,
      );
      setHistoryModalData(data);
    } catch {
      message.error('加载历史数据失败');
    } finally {
      setHistoryModalLoading(false);
    }
  }, [pointId, historyTimeRange]);

  // 打开历史数据弹窗
  const handleOpenHistoryModal = useCallback(() => {
    setHistoryModalOpen(true);
    // 延迟加载，给弹窗动画时间
    setTimeout(() => {
      loadHistoryModalData();
    }, 0);
  }, [loadHistoryModalData]);

  useEffect(() => {
    loadPointInfo();
    loadHistoryData();
  }, [loadPointInfo, loadHistoryData]);

  // ==========================================================
  // 实时数据采集
  // ==========================================================
  useEffect(() => {
    if (!realtimeEnabled || !pointInfo) return;

    const interval = setInterval(async () => {
      try {
        const value = await readPointValue(pointId);
        if (value !== null) {
          setCurrentValue(value);
          const now = new Date().toISOString();
          setRealtimeData((prev) => {
            const next = [...prev, { time: now, value }];
            // 保留最近 50 个点
            if (next.length > 50) return next.slice(next.length - 50);
            return next;
          });
          // 记录中
          if (recording) {
            setRecordCount((c) => c + 1);
            recordedDataRef.current.push({ time: now, value });
          }
        }
      } catch {
        // 静默失败
      }
    }, 2000);

    realtimeTimerRef.current = interval as unknown as NodeJS.Timeout;
    return () => {
      clearInterval(interval);
    };
  }, [realtimeEnabled, pointInfo, pointId, connectionId, recording]);

  useEffect(() => {
    return () => {
      if (realtimeTimerRef.current) clearInterval(realtimeTimerRef.current);
    };
  }, []);

  // ==========================================================
  // 统计指标
  // ==========================================================
  const realtimeValues = useMemo(
    () => realtimeData.map((d) => d.value),
    [realtimeData],
  );
  const realtimeStats = useMemo(() => calcStats(realtimeValues), [realtimeValues]);

  const historyValues = useMemo(() => historyData.map((d) => d.value), [historyData]);
  const historyStats = useMemo(() => calcStats(historyValues), [historyValues]);

  // ==========================================================
  // 数据记录
  // ==========================================================
  const handleToggleRecording = useCallback(() => {
    if (recording) {
      exportRecordingCSV(recordedDataRef.current, pointInfo?.name || 'point');
      recordedDataRef.current = [];
      setRecording(false);
    } else {
      recordedDataRef.current = [];
      setRecording(true);
      setRecordCount(0);
      message.success('开始记录数据，每 2 秒采集一次');
    }
  }, [recording, pointInfo]);

  // ==========================================================
  // 导出报告
  // ==========================================================
  const handleExportReport = useCallback(() => {
    if (realtimeData.length === 0 && historyData.length === 0) {
      message.warning('暂无数据可导出');
      return;
    }
    // 模拟导出 CSV
    const rows = ['时间,数值'];
    const allData = [...historyData, ...realtimeData];
    allData.forEach((d) => {
      rows.push(`${d.time},${d.value}`);
    });
    const csv = rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${pointInfo?.name || 'report'}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    message.success('报告已导出');
  }, [realtimeData, historyData, pointInfo]);

  // ==========================================================
  // 更改分组
  // ==========================================================
  const handleChangeGroup = useCallback(async (groupId: string | null) => {
    try {
      await opcSetPointGroup(connectionId, pointId, groupId);
      const targetName = groupId
        ? groupList.find((g) => g.id === groupId)?.name || '未分组'
        : '未分组';
      setGroupName(targetName);
      setGroupModalOpen(false);
      message.success(`已移至 "${targetName}"`);
    } catch {
      message.error('移动分组失败');
    }
  }, [connectionId, pointId, groupList]);

  // ==========================================================
  // 图表配置
  // ==========================================================
  const realtimeChartOption = useMemo(() => ({
    grid: { top: 10, right: 16, bottom: 24, left: 44 },
    xAxis: {
      type: 'category',
      data: realtimeData.map((d) => {
        const t = new Date(d.time);
        return `${t.getMinutes().toString().padStart(2, '0')}:${t.getSeconds().toString().padStart(2, '0')}`;
      }),
      axisLabel: { fontSize: 10, color: '#a0aec0' },
      axisLine: { lineStyle: { color: '#e2e8f0' } },
    },
    yAxis: {
      type: 'value',
      axisLabel: { fontSize: 10, color: '#a0aec0' },
      splitLine: { lineStyle: { color: '#edf2f7', type: 'dashed' as const } },
    },
    tooltip: {
      trigger: 'axis' as const,
      formatter: (params: any) => {
        const p = params[0];
        return `${p.axisValue}<br/>数值: <b>${p.value?.toFixed(2)}</b> ${pointInfo?.unit || ''}`;
      },
    },
    series: [{
      type: 'line',
      data: realtimeValues,
      smooth: true,
      symbol: 'none',
      lineStyle: { color: '#1890ff', width: 2 },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(24,144,255,0.25)' },
            { offset: 1, color: 'rgba(24,144,255,0.02)' },
          ],
        },
      },
    }],
  }), [realtimeData, realtimeValues, pointInfo]);

  const historyChartOption = useMemo(() => ({
    grid: { top: 10, right: 16, bottom: 24, left: 44 },
    xAxis: {
      type: 'category',
      data: historyData.map((d) => {
        const t = new Date(d.time);
        return `${t.getHours().toString().padStart(2, '0')}:${t.getMinutes().toString().padStart(2, '0')}`;
      }),
      axisLabel: { fontSize: 10, color: '#a0aec0', interval: 30 },
      axisLine: { lineStyle: { color: '#e2e8f0' } },
    },
    yAxis: {
      type: 'value',
      axisLabel: { fontSize: 10, color: '#a0aec0' },
      splitLine: { lineStyle: { color: '#edf2f7', type: 'dashed' as const } },
    },
    tooltip: {
      trigger: 'axis' as const,
      formatter: (params: any) => {
        const p = params[0];
        return `${p.axisValue}<br/>数值: <b>${p.value?.toFixed(2)}</b> ${pointInfo?.unit || ''}`;
      },
    },
    dataZoom: [{
      type: 'inside' as const,
      start: 0,
      end: 100,
    }],
    series: [{
      type: 'line',
      data: historyValues,
      smooth: false,
      symbol: 'none',
      lineStyle: { color: '#52c41a', width: 1.5 },
    }],
  }), [historyData, historyValues, pointInfo]);

  // 分布直方图（基于实时数据，随实时数据更新）
  const histogramOption = useMemo(() => {
    if (realtimeValues.length === 0) return {};
    const values = realtimeValues;
    const minVal = Math.min(...values);
    const maxVal = Math.max(...values);
    const range = maxVal - minVal || 1;
    const binCount = 10;
    const binWidth = range / binCount;
    const bins: number[] = new Array(binCount).fill(0);
    const labels: string[] = [];

    values.forEach((v) => {
      const idx = Math.min(Math.floor((v - minVal) / binWidth), binCount - 1);
      bins[idx]++;
    });

    // 使用区间中心值作为标签（与原版一致）
    for (let i = 0; i < binCount; i++) {
      const center = minVal + (i + 0.5) * binWidth;
      labels.push(center.toFixed(1));
    }

    return {
      grid: { top: 20, right: 10, bottom: 30, left: 30 },
      xAxis: {
        type: 'category',
        data: labels,
        axisLabel: { fontSize: 9, color: '#718096', rotate: 45 },
      },
      yAxis: {
        type: 'value',
        axisLabel: { fontSize: 9, color: '#718096' },
        splitLine: { lineStyle: { color: '#e2e8f0' } },
      },
      tooltip: {
        trigger: 'axis' as const,
        formatter: (params: any) => {
          const idx = params[0]?.dataIndex ?? 0;
          const low = (minVal + idx * binWidth).toFixed(2);
          const high = (minVal + (idx + 1) * binWidth).toFixed(2);
          return `区间: ${low} ~ ${high}<br/>频次: <b>${params[0]?.value}</b>`;
        },
      },
      series: [{
        type: 'bar',
        data: bins,
        itemStyle: { color: '#a78bfa' },
        barWidth: '60%',
      }],
    };
  }, [realtimeValues]);

  // 箱线图（基于实时数据，随实时数据更新）
  const boxplotOption = useMemo(() => {
    if (realtimeValues.length < 5) return {};
    const sorted = [...realtimeValues].sort((a, b) => a - b);
    const n = sorted.length;
    const dataMin = sorted[0];
    const dataMax = sorted[n - 1];
    const q1 = sorted[Math.floor(n * 0.25)];
    const median = sorted[Math.floor(n * 0.5)];
    const q3 = sorted[Math.floor(n * 0.75)];

    return {
      grid: { top: 20, right: 10, bottom: 30, left: 40 },
      xAxis: {
        type: 'category',
        data: [''],
        axisLabel: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLabel: { fontSize: 10, color: '#718096' },
        splitLine: { lineStyle: { color: '#e2e8f0' } },
      },
      tooltip: {
        trigger: 'item' as const,
        formatter: () =>
          `最小值: <b>${dataMin.toFixed(4)}</b><br/>` +
          `Q1 (下四分位): <b>${q1.toFixed(4)}</b><br/>` +
          `中位数: <b>${median.toFixed(4)}</b><br/>` +
          `Q3 (上四分位): <b>${q3.toFixed(4)}</b><br/>` +
          `最大值: <b>${dataMax.toFixed(4)}</b>`,
      },
      series: [{
        type: 'boxplot',
        data: [[dataMin, q1, median, q3, dataMax]],
        itemStyle: {
          color: '#5b8def',
          borderColor: '#3b6fd4',
        },
        boxWidth: [14, '40%'],
      }],
    };
  }, [realtimeValues]);

  // ==========================================================
  // 返回
  // ==========================================================
  const handleBack = useCallback(() => {
    navigate(`/project/home/data/point?connection_id=${connectionId}`);
  }, [navigate, connectionId]);

  // ==========================================================
  // 渲染
  // ==========================================================
  if (loading) {
    return (
      <>
        <TopBar title="工艺设计" mainContent={<Navbar /> } />
        <div className="pdi-page pdi-loading">
          <Spin size="large" tip="加载点位数据..." />
        </div>
      </>
    );
  }

  if (!pointInfo) {
    return (
      <>
        <TopBar title="工艺设计" />
        <div className="pdi-page pdi-loading">
          <Empty description="点位不存在" />
          <Button type="primary" onClick={handleBack} style={{ marginTop: 16 }}>
            返回列表
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <TopBar title="工艺设计" mainContent={<Navbar /> } />
      <div className="pdi-page">
        {/* 顶部导航栏 */}
        <div className="pdi-toolbar">
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            className="pdi-back-btn"
          >
            返回点位列
          </Button>
          <Space>
            <Button
              icon={<FolderOpenOutlined />}
              onClick={() => setGroupModalOpen(true)}
            >
              更改分组
            </Button>
            <Button
              icon={<HistoryOutlined />}
              onClick={handleOpenHistoryModal}
            >
              历史数据
            </Button>
            <Button
              icon={<ExportOutlined />}
              onClick={handleExportReport}
            >
              导出报告
            </Button>
          </Space>
        </div>

        {/* 头部信息卡片 */}
        <div className="pdi-header-section">
          <Card className="pdi-header-card" bordered={false}>
            <div className="pdi-header-top">
              <div className="pdi-header-left">
                <div className="pdi-point-name">{pointInfo.name}</div>
                <div className="pdi-point-meta">
                  <Tag color="blue">{pointInfo.node_id}</Tag>
                  <Tag>{groupName}</Tag>
                  <Tag
                    color={
                      pointInfo.status === 'Good'
                        ? 'success'
                        : pointInfo.status === 'Bad'
                          ? 'error'
                          : 'warning'
                    }
                  >
                    {pointInfo.status}
                  </Tag>
                </div>
               {/* 记录状态 */}
            <div className="pdi-recording-bar">
              <Button
                danger={recording}
                type={recording ? 'primary' : 'default'}
                icon={recording ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                onClick={handleToggleRecording}
              >
                {recording ? '停止记录' : '开始记录'}
              </Button>
              {recording && (
                <span className="pdi-record-indicator">
                  <span className="pdi-record-dot" />
                  正在记录中 · 已采集 {recordCount} 条
                </span>
              )}
            </div>
              </div>
              <div className="pdi-header-right">
                <div className="pdi-current-label">当前值</div>
                <div className="pdi-current-value">
                  {currentValue !== null ? currentValue.toFixed(2) : '---'}
                  {pointInfo.unit && <span className="pdi-current-unit"> {pointInfo.unit}</span>}
                </div>
                <div className="pdi-realtime-toggle">
                  <span>实时刷新</span>
                  <Switch
                    size="small"
                    checked={realtimeEnabled}
                    onChange={setRealtimeEnabled}
                    style={{ marginLeft: 8 }}
                  />
                </div>
              </div>
            </div>

            {/* 记录状态
            <div className="pdi-recording-bar">
              <Button
                danger={recording}
                type={recording ? 'primary' : 'default'}
                icon={recording ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                onClick={handleToggleRecording}
              >
                {recording ? '停止记录' : '开始记录'}
              </Button>
              {recording && (
                <span className="pdi-record-indicator">
                  <span className="pdi-record-dot" />
                  正在记录中 · 已采集 {recordCount} 条
                </span>
              )}
            </div> */}
          </Card>
        </div>

        <div className="pdi-content">
          {/* 统计卡片 */}
          <div className="pdi-stats-section">
            <Row gutter={[16, 16]}>
              <Col xs={12} sm={6}>
                <Card className="pdi-stat-card pdi-stat-avg" bordered={false}>
                  <Statistic
                    title="平均值"
                    value={realtimeStats.avg}
                    precision={2}
                    suffix={pointInfo.unit}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card className="pdi-stat-card pdi-stat-max" bordered={false}>
                  <Statistic
                    title="最大值"
                    value={realtimeStats.max}
                    precision={2}
                    suffix={pointInfo.unit}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card className="pdi-stat-card pdi-stat-min" bordered={false}>
                  <Statistic
                    title="最小值"
                    value={realtimeStats.min}
                    precision={2}
                    suffix={pointInfo.unit}
                    valueStyle={{ color: '#faad14' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card className="pdi-stat-card pdi-stat-std" bordered={false}>
                  <Statistic
                    title="标准差"
                    value={realtimeStats.std}
                    precision={4}
                    suffix={pointInfo.unit}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Card>
              </Col>
            </Row>
          </div>

          {/* 图表区域 */}
          <div className="pdi-charts-section">
            <Row gutter={[16, 16]}>
              {/* 实时趋势图 */}
              <Col xs={24} lg={12}>
                <Card
                  className="pdi-chart-card"
                  bordered={false}
                  title={
                    <span>
                      <LineChartOutlined style={{ color: '#1890ff', marginRight: 6 }} />
                      实时趋势
                    </span>
                  }
                  extra={<Tag color="blue">最近 50 点</Tag>}
                >
                  {realtimeData.length > 0 ? (
                    <ReactECharts
                      option={realtimeChartOption}
                      style={{ height: 280 }}
                      notMerge
                    />
                  ) : (
                    <Empty description="等待实时数据..." image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  )}
                </Card>
              </Col>

              {/* 历史数据图 */}
              <Col xs={24} lg={12}>
                <Card
                  className="pdi-chart-card"
                  bordered={false}
                  title={
                    <span>
                      <LineChartOutlined style={{ color: '#52c41a', marginRight: 6 }} />
                      历史数据
                    </span>
                  }
                  extra={
                    <Space size={4}>
                      <Tag color="green">最近 30 分钟</Tag>
                      <Button
                        type="link"
                        size="small"
                        icon={<ReloadOutlined />}
                        onClick={loadHistoryData}
                      >
                        刷新
                      </Button>
                    </Space>
                  }
                >
                  {historyData.length > 0 ? (
                    <ReactECharts
                      option={historyChartOption}
                      style={{ height: 280 }}
                      notMerge
                    />
                  ) : (
                    <Empty description="暂无历史数据" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  )}
                </Card>
              </Col>

              {/* 分布直方图 */}
              <Col xs={24} md={12}>
                <Card
                  className="pdi-chart-card"
                  bordered={false}
                  title={
                    <span>
                      <LineChartOutlined style={{ color: '#722ed1', marginRight: 6 }} />
                      数值分布
                    </span>
                  }
                  extra={<Tag color="purple">直方图</Tag>}
                >
                  {historyData.length > 0 ? (
                    <ReactECharts
                      option={histogramOption}
                      style={{ height: 260 }}
                      notMerge
                    />
                  ) : (
                    <Empty description="暂无数据" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  )}
                </Card>
              </Col>

              {/* 箱线图 */}
              <Col xs={24} md={12}>
                <Card
                  className="pdi-chart-card"
                  bordered={false}
                  title={
                    <span>
                      <LineChartOutlined style={{ color: '#2f54eb', marginRight: 6 }} />
                      箱线图
                    </span>
                  }
                  extra={<Tag color="geekblue">Q1/Q2/Q3</Tag>}
                >
                  {historyData.length >= 4 ? (
                    <ReactECharts
                      option={boxplotOption}
                      style={{ height: 260 }}
                      notMerge
                    />
                  ) : (
                    <Empty description="数据不足" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  )}
                </Card>
              </Col>
            </Row>
          </div>
        </div>

        {/* 历史数据弹窗 */}
        <Modal
          title={
            <Space>
              <HistoryOutlined />
              <span>历史数据 - {pointInfo?.name || pointId}</span>
            </Space>
          }
          open={historyModalOpen}
          onCancel={() => setHistoryModalOpen(false)}
          width={900}
          footer={null}
          destroyOnClose
        >
          {/* 时间范围选择 */}
          <div style={{ marginBottom: 16 }}>
            <Space>
              <span>时间范围：</span>
              <DatePicker.RangePicker
                value={[dayjs(historyTimeRange[0]), dayjs(historyTimeRange[1])]}
                onChange={(dates) => {
                  if (dates?.[0] && dates?.[1]) {
                    const start = dates[0].format('YYYY-MM-DD');
                    const end = dates[1].format('YYYY-MM-DD');
                    setHistoryTimeRange([start, end]);
                  }
                }}
                allowClear={false}
              />
              <Button type="primary" onClick={loadHistoryModalData} loading={historyModalLoading}>
                查询
              </Button>
            </Space>
          </div>

          {/* 摘要 */}
          <div style={{ marginBottom: 12, color: '#718096', fontSize: 14 }}>
            共找到 <b>{historyModalData.length}</b> 条历史记录
            {pointInfo && <span style={{ marginLeft: 12 }}>节点 ID: <Tag color="blue">{pointInfo.node_id}</Tag></span>}
          </div>

          {/* 数据表格 */}
          <Table
            dataSource={historyModalData.map((d, i) => ({ ...d, key: i }))}
            loading={historyModalLoading}
            size="small"
            scroll={{ y: 400 }}
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `共 ${t} 条` }}
            columns={[
              {
                title: '时间',
                dataIndex: 'time',
                key: 'time',
                render: (t: string) => {
                  const d = new Date(t);
                  return d.toLocaleString('zh-CN', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  });
                },
              },
              {
                title: '数值',
                dataIndex: 'value',
                key: 'value',
                render: (v: number) => <b>{v?.toFixed(4)}</b>,
              },
              {
                title: '状态',
                key: 'status',
                render: () => <Tag color="success">Good</Tag>,
                width: 80,
              },
            ]}
          />
        </Modal>

        {/* 更改分组弹窗 */}
        <Modal
          title="更改点位分组"
          open={groupModalOpen}
          onCancel={() => setGroupModalOpen(false)}
          footer={null}
          destroyOnClose
        >
          <div className="pdi-move-list">
            <div
              className="pdi-move-item"
              onClick={() => handleChangeGroup(null)}
            >
              <ThunderboltOutlined style={{ fontSize: 18 }} />
              <span>未分组</span>
            </div>
            {groupList.map((g) => (
              <div
                key={g.id}
                className="pdi-move-item"
                onClick={() => handleChangeGroup(g.id)}
              >
                <FolderOpenOutlined style={{ fontSize: 18, color: '#ffb53b' }} />
                <span>{g.name}</span>
              </div>
            ))}
          </div>
        </Modal>
      </div>
    </>
  );
};

export default PointData;
